import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { APPOINTMENT_STATUSES, AppointmentStatus } from '@bb/domain/dist/types';
import {
  CircleButton,
  Color,
  Divider,
  Flex,
  Icon,
  Title,
} from '@bb/elements-web';

import { useBreakpoint } from 'hooks';
import { INVALID_STATUSES } from '../../constants';
import ShowingDetailsContext from '../../ShowingDetailsV2/ShowingDetailsContext';
import ActionButtons from './ActionButtons';

type AppointmentStatusInsideDetails =
  | Exclude<AppointmentStatus, (typeof INVALID_STATUSES)[number]>
  | 'complete'
  | 'listingContactTimeChange';

const isValidStatus = (status: any): status is AppointmentStatusInsideDetails =>
  APPOINTMENT_STATUSES.includes(status) && !INVALID_STATUSES.includes(status);
const showingStatusMap: Record<AppointmentStatusInsideDetails, string> = {
  new: 'Requested',
  attempted: 'Requested',
  time_change: 'Time Change',
  listingContactTimeChange: 'Suggested',
  suggested_time_change: 'Suggested',
  draft: 'Draft',
  confirm: 'Confirmed',
  complete: 'Completed',
  cancel: 'Cancelled',
  deny: 'Denied',
  time_passed: 'Time Passed',
};
const getStatusLabel = (
  status: AppointmentStatus,
  isPassed?: boolean,
  listingContactTimeChange?: boolean,
) => {
  if (!isValidStatus(status)) {
    return '';
  }
  let convertedStatus: AppointmentStatusInsideDetails = status;
  if (status === 'confirm' && isPassed) {
    convertedStatus = 'complete';
  } else if (status === 'time_change' && listingContactTimeChange) {
    convertedStatus = 'listingContactTimeChange';
  }
  return showingStatusMap[convertedStatus];
};

const Header: React.FC = () => {
  const {
    appointment: { status, listingContactTimeChange },
    fromTourPage,
    isPassed,
  } = useContext(ShowingDetailsContext);
  const history = useHistory();
  const { md } = useBreakpoint();
  const isMobile = !md;
  const titleSize = isMobile ? 4 : 2;
  const resultLabel = getStatusLabel(
    status,
    isPassed,
    listingContactTimeChange,
  );

  const goToShowingCalendar = () => history.push(`/my_business/showings/buyer`);

  return (
    <>
      <StyledHeaderContainer
        align="center"
        justify="center"
        wrap="wrap"
        $isMobile={isMobile}
        $fromTourPage={fromTourPage}
      >
        <StyledTitleContainer gap="small" align="center">
          {!fromTourPage && (
            <CircleButton
              dataCy="showingDetails-back-button"
              type="default"
              size="large"
              onClick={goToShowingCalendar}
              icon={
                <Icon icon="fullArrowLeftOutlined" size={16} color="inherit" />
              }
            />
          )}
          <Title level={titleSize} dataCy="showingDetails-Title">
            {`Showing ${resultLabel}`}
          </Title>
        </StyledTitleContainer>
        <ActionButtons />
      </StyledHeaderContainer>
      {!fromTourPage && <Divider />}
    </>
  );
};

export default Header;

const StyledHeaderContainer = styled(Flex)<{
  $isMobile: boolean;
  $fromTourPage: boolean;
}>`
  width: 100%;
  ${({ $fromTourPage }) => $fromTourPage && 'margin-top:-30px;'}
  ${({ $isMobile }) => $isMobile && 'margin-top:-15px; margin-bottom:-5px;'}

  & > div:first-child {
    flex-grow: 1;
  }
`;
const StyledTitleContainer = styled(Flex)`
  justify-content: start;
  margin-bottom: 0px;
  & > .ant5-btn {
    margin-top: 3px;
    margin-right: 6px;
  }
  & > .ant5-typography {
    color: ${Color.BLACK};
    margin-top: 5px;
    margin-bottom: 4px;
  }
`;
